import type { MessageDescriptor } from "@lingui/core";

import { msg } from "@lingui/core/macro";

import {
  AIFeedbackCategory,
  AlertKind,
  AlertType,
  ClinicalStudies,
  ECGMeasurementMode,
  ECGQualityFlag,
  ECGRelatedQualityFlag,
  HealthProPhysicians,
  HealthProReportDiagnosis,
  InformedConsent,
  LocaleTag,
  PatientMonitoringCallStatus,
  PatientMonitoringReportStatus,
  PatientMonitoringType,
  PatientMonitoringWatchKind,
  PatientMonitoringWatchStatus,
  PatientState,
  PatientStatus,
  PPGMeasurementMode,
  PPGQualityFlag,
  PPGRelatedQualityFlag,
  QuestionnaireTypes,
  Race,
  StrictPPGConditions,
  SupportedLanguages,
  UserFeatureFlag,
  UserStatus,
} from "../api";
import {
  type EnumLabel,
  resolveEnumValueLabel,
} from "../components/ui-kit/fields/utils";
import { languages, localeLabel } from "../languages";

/** Picker labels for every language the API supports, from its record. */
export const supportedLanguages = Object.fromEntries(
  languages.map((language) => [language.code, localeLabel(language.code)]),
) as Record<SupportedLanguages, string>;

/** Labels for a stored locale tag, variants included ("ar-SA"). */
export const localeTags = Object.fromEntries(
  Object.values(LocaleTag).map((tag) => [tag, localeLabel(tag)]),
) as Record<LocaleTag, string>;

export const userFeatureFlags: Record<UserFeatureFlag, MessageDescriptor> = {
  [UserFeatureFlag.fe_daily_monitoring_dashboard]: msg`Daily monitoring dashboard`,
  [UserFeatureFlag.fe_app_only_user]: msg`App-only user`,
  [UserFeatureFlag.app_rhythm_monitoring]: msg`Rhythm monitoring`,
  [UserFeatureFlag.app_confirm_manual_entry]: msg`Confirm manual entry`,
  [UserFeatureFlag.app_ui_v2]: msg`New app dashboard (v2)`,
  [UserFeatureFlag.app_kiosk_mode]: msg`Kiosk mode`,
};

export const userFeatureFlagHints: Record<UserFeatureFlag, MessageDescriptor> =
  {
    [UserFeatureFlag.fe_daily_monitoring_dashboard]: msg`Daily telemonitoring overview: users measured today plus a quick data view.`,
    [UserFeatureFlag.fe_app_only_user]: msg`Marks an app-only research-partnership user; this frontend hides its UI for them.`,
    [UserFeatureFlag.app_rhythm_monitoring]: msg`Rhythm monitoring in the app profile.`,
    [UserFeatureFlag.app_confirm_manual_entry]: msg`Confirmation dialog after a manual entry (symptoms, vitals, questionnaires), with a chance to fix mistakes.`,
    [UserFeatureFlag.app_ui_v2]: msg`New app dashboard design.`,
    [UserFeatureFlag.app_kiosk_mode]: msg`For patients using a locked kiosk-mode phone.`,
  };

export const patientStates: Record<PatientState, MessageDescriptor> = {
  [PatientState.normal]: msg`Normal`,
  [PatientState.high_risk]: msg`High risk`,
};

// Keys are numeric enum values, so consumers that iterate this map
// (`valuesToMap`, the admin status breakdown) always get ascending enum order
// regardless of the order written here — keep the two in sync so the source
// reads the way the dropdowns render.
export const patientStatuses: Record<PatientStatus, MessageDescriptor> = {
  [PatientStatus.test]: msg`Test`,
  [PatientStatus.active]: msg`Active`,
  [PatientStatus.not_active]: msg`Not active`,
  [PatientStatus.archived]: msg`Archived`,
  [PatientStatus.deceased]: msg`Deceased`,
  [PatientStatus.onboarded]: msg`Onboarded`,
};

export const userStatuses: Record<UserStatus, MessageDescriptor> = {
  [UserStatus.not_active]: msg`Not active`,
  [UserStatus.active]: msg`Active`,
  [UserStatus.reset_by_admin]: msg`Pass. reset (admin)`,
  [UserStatus.reset_by_user]: msg`Pass. reset (user)`,
  [UserStatus.temporary_password]: msg`Temporary pass.`,
  [UserStatus.archived]: msg`Archived`,
};

export const monitoringTypes: Record<PatientMonitoringType, MessageDescriptor> =
  {
    [PatientMonitoringType.self_measurement]: msg`Self-measurement`,
    [PatientMonitoringType.physician_supervision]: msg`Physician supervision`,
    [PatientMonitoringType.testing]: msg`Testing`,
    [PatientMonitoringType.screening]: msg`Screening`,
  };

export const informedConsent: Record<InformedConsent, MessageDescriptor> = {
  [InformedConsent.none]: msg`None`,
  [InformedConsent.telemonitoring]: msg`Telemonitoring`,
  [InformedConsent.paper]: msg`Paper`,
};

export const ppgRelatedFlag: Record<PPGRelatedQualityFlag, MessageDescriptor> =
  {
    [PPGRelatedQualityFlag.wrong]: msg`Wrong`,
    [PPGRelatedQualityFlag.ok]: msg`OK`,
    [PPGRelatedQualityFlag.low_ppg_quality]: msg`Low PPG Quality`,
    [PPGRelatedQualityFlag.ignore_data]: msg`Ignore`,
    [PPGRelatedQualityFlag.manual_value_override]: msg`Manual value override`,
  };

export const ecgRelatedFlag: Record<ECGRelatedQualityFlag, MessageDescriptor> =
  {
    [ECGRelatedQualityFlag._0]: msg`Wrong`,
    [ECGRelatedQualityFlag._1]: msg`OK`,
    [ECGRelatedQualityFlag._2]: msg`Low ECG Quality`,
    [ECGRelatedQualityFlag._3]: msg`Ignore`,
    [ECGRelatedQualityFlag._4]: msg`Manual value override`,
  };

/**
 * Postures for conditions 1-3; 4-6 occur only in clinical research and keep
 * their bare numbers. Keys are the API wire values — only labels are display
 * text, so map at the render layer, never over the stored value (the legacy
 * plots key `colorMapping`/`defaultHidden` on the raw condition). Clinical
 * studies show the raw "Condition N" and deliberately skip this map.
 */
export const ppgConditionLabels: Record<StrictPPGConditions, EnumLabel> = {
  [StrictPPGConditions["Condition 1"]]: msg`Standing`,
  [StrictPPGConditions["Condition 2"]]: msg`Supine`,
  [StrictPPGConditions["Condition 3"]]: msg`Sitting`,
  [StrictPPGConditions["Condition 4"]]: "Condition 4",
  [StrictPPGConditions["Condition 5"]]: "Condition 5",
  [StrictPPGConditions["Condition 6"]]: "Condition 6",
};

/**
 * Display text for a raw condition value. Values outside the map pass through
 * unchanged — `resolveEnumLabel` alone would blank them, and they do occur:
 * `"rest"`, `"--"`, and the comma-joined hue for a multi-condition row.
 */
export function ppgConditionText(value: string | null | undefined): string {
  if (!value) return "";
  return resolveEnumValueLabel(ppgConditionLabels, value);
}

/** Same, for the `ppgConditions` row field — a list of raw condition values. */
export function ppgConditionsText(
  values: readonly string[] | null | undefined,
): string {
  return (values ?? []).map(ppgConditionText).join(", ");
}

/**
 * Display text for a plot hue. A multi-PPG row groups under a comma-joined
 * condition list ("Condition 1,Condition 2"), which only maps once split;
 * hues that hold no comma (e.g. `data_source`) pass through unchanged.
 */
export function ppgConditionHueLabel(hue: string): string {
  return ppgConditionsText(hue.split(","));
}

export const ppgMeasModes: Record<PPGMeasurementMode, MessageDescriptor> = {
  [PPGMeasurementMode.external_data]: msg`external`,
  [PPGMeasurementMode.seerlinq_supervision]: msg`Seerlinq supervison`,
  [PPGMeasurementMode.physician_supervison]: msg`physician supervision`,
  [PPGMeasurementMode.home_monitoring]: msg`home-monitoring`,
  [PPGMeasurementMode.synthetic]: msg`synthetic data`,
};

export const ppgQuality: Record<PPGQualityFlag, MessageDescriptor> = {
  [PPGQualityFlag.unknown]: msg`Unknown`,
  [PPGQualityFlag.data_error]: msg`Data error`,
  [PPGQualityFlag.ok]: msg`OK`,
  [PPGQualityFlag.low_signal_quality]: msg`Low signal quality`,
  [PPGQualityFlag.artefacts]: msg`Artefacts`,
  [PPGQualityFlag.short_signal]: msg`Short`,
  [PPGQualityFlag.extra_circumstance]: msg`Extra`,
  [PPGQualityFlag.test_measurement]: msg`Testing`,
};

export const ecgMeasModes: Record<ECGMeasurementMode, MessageDescriptor> = {
  [ECGMeasurementMode.external_data]: msg`external`,
  [ECGMeasurementMode.home_monitoring]: msg`home-monitoring`,
  [ECGMeasurementMode.physician_supervison]: msg`physician supervision`,
  [ECGMeasurementMode.seerlinq_supervision]: msg`Seerlinq supervison`,
  [ECGMeasurementMode.synthetic]: msg`synthetic data`,
};

export const ecgQuality: Record<ECGQualityFlag, MessageDescriptor> = {
  [ECGQualityFlag.data_error]: msg`Data error`,
  [ECGQualityFlag.low_signal_quality]: msg`Low signal quality`,
  [ECGQualityFlag.ok]: msg`OK`,
  [ECGQualityFlag.unknown]: msg`Unknown`,
};

export const alertTypes: Record<AlertType, MessageDescriptor> = {
  [AlertType.low_critical]: msg`CRITICAL low`,
  [AlertType.low_alert]: msg`Alert low`,
  [AlertType.low_warning]: msg`warning low`,
  [AlertType.normal]: msg`normal`,
  [AlertType.high_warning]: msg`warning high`,
  [AlertType.high_alert]: msg`Alert high`,
  [AlertType.high_critical]: msg`CRITICAL high`,
};

export const alertKinds: Record<AlertKind, MessageDescriptor> = {
  [AlertKind.medical]: msg`Medical`,
  [AlertKind.compliance]: msg`Compliance`,
  [AlertKind.technical]: msg`Technical`,
};

// Proper names — never translated.
export const healthProPhysicians: Record<HealthProPhysicians, string> = {
  [HealthProPhysicians.allan_bohm]: "Allan Böhm",
  [HealthProPhysicians.diana_sevcikova]: "Diana Ševčíková",
};

// only subset
export const healthProPatientDiagnoses = {
  [HealthProReportDiagnosis["I50.9"]]: "I50.9",
  [HealthProReportDiagnosis["I50.11"]]: "I50.11",
  [HealthProReportDiagnosis["I50.12"]]: "I50.12",
  [HealthProReportDiagnosis["I50.13"]]: "I50.13",
  [HealthProReportDiagnosis["I50.14"]]: "I50.14",
} as Record<HealthProReportDiagnosis, string>;

export const callStatuses: Record<
  PatientMonitoringCallStatus,
  MessageDescriptor
> = {
  [PatientMonitoringCallStatus.nothing]: msg`Nothing`,
  [PatientMonitoringCallStatus.to_call]: msg`To call`,
  [PatientMonitoringCallStatus.called]: msg`Called`,
};

export const reportStatuses: Record<
  PatientMonitoringReportStatus,
  MessageDescriptor
> = {
  [PatientMonitoringReportStatus.nothing]: msg`Nothing`,
  [PatientMonitoringReportStatus.to_report]: msg`To report`,
  [PatientMonitoringReportStatus.reported]: msg`Reported`,
};

export const watchStatuses: Record<
  PatientMonitoringWatchStatus,
  MessageDescriptor
> = {
  [PatientMonitoringWatchStatus.in_progress]: msg`In progress`,
  [PatientMonitoringWatchStatus.new_alert]: msg`New alert`,
  [PatientMonitoringWatchStatus.not_watching]: msg`Not watching`,
  [PatientMonitoringWatchStatus.watching]: msg`Watching`,
  [PatientMonitoringWatchStatus.from_audit]: msg`From audit`,
};
export const watchKinds: Record<PatientMonitoringWatchKind, MessageDescriptor> =
  {
    [PatientMonitoringWatchKind.none]: msg`--`,
    [PatientMonitoringWatchKind.medical]: msg`Medical room`,
    [PatientMonitoringWatchKind.tech_compliance]: msg`Technical room`,
  };

export const aiFeedbackCategories: Record<
  AIFeedbackCategory,
  MessageDescriptor
> = {
  [AIFeedbackCategory.factual_error]: msg`Factual error`,
  [AIFeedbackCategory.not_actionable]: msg`Not actionable`,
  [AIFeedbackCategory.patient_safety]: msg`Patient safety`,
};

export const aiFeedbackCategoryDescriptions: Record<
  AIFeedbackCategory,
  MessageDescriptor
> = {
  [AIFeedbackCategory.factual_error]: msg`States something the record doesn't support`,
  [AIFeedbackCategory.not_actionable]: msg`Accurate, but not worth reading`,
  [AIFeedbackCategory.patient_safety]: msg`The summary could have misled clinical judgment`,
};

export const clinicalStudies: Record<ClinicalStudies, string> = {
  [ClinicalStudies["seerlinq-lhc"]]: "Seerlinq-LHC",
};

export const questionnaireTypes = {
  [QuestionnaireTypes.eq_5d_5l]: "EQ-5D-5L",
  [QuestionnaireTypes.customer_feedback]: "Customer feedback",
} as Record<QuestionnaireTypes, string>;

if (import.meta.env.VITE_QUESTIONNAIRES_DUMMY) {
  questionnaireTypes[QuestionnaireTypes.dummy] = "Dummy";
}

export const RequiredDataType: Record<string, MessageDescriptor> = {
  admin: msg`administrative`,
  diagnosis: msg`diagnosis`,
  "medical-data": msg`medical data`,
  symptom: msg`symptom`,
  questionnaire: msg`questionnaire`,
  ppg: msg`PPG`,
  computed: msg`SQ-computed`,
  derived: msg`PPG-derived`,
};

export const raceOptions = Object.values(Race).map((v) => ({
  value: v,
  label: v,
}));
